import React, { useMemo } from "react";

type StageRow = {
  user_id?: string;
  user_name?: string | null;
  stage?: string | null;
  updatedBy?: string | null;
  updatedByName?: string | null;
  updatedAt?: string | Date | null;
  type?: string;
};

type Member = {
  _id?: string;
  id?: string;
  name?: string;
  fullName?: string;
  email?: string;
  hierarchyId?: string;
  designation?: string;
};

type Props = {
  stages?: StageRow[] | null;
  members?: Member[] | any;
  leadStage?: string | null;
  leadName?: string | null;
  assignmentMode?: "separate" | "shared" | string | null;
  hierarchyMap?: Record<string, string>;
};

const normId = (v: any) =>
  String(v ?? "")
    .replace(/['"]/g, "")
    .trim();

const avatarPalette = [
  { bg: "#F9F0F4", color: "#6B1630" },
  { bg: "#DBEAFE", color: "#2563EB" },
  { bg: "#D1FAE5", color: "#059669" },
  { bg: "#FFEDD5", color: "#EA580C" },
  { bg: "#FCE7F3", color: "#DB2777" },
];

function formatStage(stage?: string | null) {
  const s = String(stage || "").trim();
  if (!s) return "Initial";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatWhen(value?: string | Date | null) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

/**
 * Admin Stage Details — one clear row per assignee with their own stage.
 */
const StageDetailsPanel: React.FC<Props> = ({
  stages,
  members,
  leadStage,
  leadName,
  assignmentMode,
  hierarchyMap = {},
}) => {
  const memberList: Member[] = useMemo(() => {
    if (Array.isArray(members)) return members.filter(Boolean);
    if (members && typeof members === "object") return [members];
    return [];
  }, [members]);

  const rows = useMemo(() => {
    const raw = Array.isArray(stages) ? stages : [];
    const stageByUser = new Map<string, StageRow>();

    for (const s of raw) {
      const uid = normId(s?.user_id);
      if (!uid) continue; // drop shared/orphan rows without an assignee
      const prev = stageByUser.get(uid);
      const nextTs = s?.updatedAt ? new Date(s.updatedAt as any).getTime() : 0;
      const prevTs = prev?.updatedAt
        ? new Date(prev.updatedAt as any).getTime()
        : 0;
      if (!prev || nextTs >= prevTs) stageByUser.set(uid, s);
    }

    const didChangeStage = (row?: StageRow | null) =>
      Boolean(normId(row?.updatedBy) || String(row?.updatedByName || "").trim());

    // Only people who actually changed the stage — not every assignee.
    if (memberList.length) {
      return memberList
        .map((m) => {
          const uid = normId(m._id || m.id);
          const stage = stageByUser.get(uid);
          return {
            user_id: uid,
            user_name: m.name || m.fullName || m.email || stage?.user_name || "",
            stage: stage?.stage || leadStage || "Initial",
            updatedBy: stage?.updatedBy || null,
            updatedByName: stage?.updatedByName || null,
            updatedAt: stage?.updatedAt || null,
            designation:
              (m.hierarchyId && hierarchyMap[m.hierarchyId]) ||
              m.designation ||
              "—",
            fromMember: true,
          };
        })
        .filter((row) => didChangeStage(row));
    }

    return Array.from(stageByUser.values())
      .filter((s) => didChangeStage(s))
      .map((s) => ({
        user_id: normId(s.user_id),
        user_name: s.user_name || "",
        stage: s.stage || leadStage || "Initial",
        updatedBy: s.updatedBy || null,
        updatedByName: s.updatedByName || null,
        updatedAt: s.updatedAt || null,
        designation: "—",
        fromMember: false,
      }));
  }, [stages, memberList, hierarchyMap, leadStage]);

  const separate =
    String(assignmentMode || "separate").toLowerCase() !== "shared";
  const lastTouched = rows
    .filter((r) => r.updatedAt)
    .slice()
    .sort(
      (a, b) =>
        new Date(b.updatedAt as any).getTime() -
        new Date(a.updatedAt as any).getTime()
    )[0];

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-2xl border border-[#F9F0F4] bg-gradient-to-r from-[#F5F3FF] to-[#F9F0F4] px-5 py-4">
        <div className="relative z-[1] flex flex-wrap items-start gap-6">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-[#6B7280]">
              {leadName ? String(leadName) : "Lead"}
            </p>
            <p className="mt-1 text-sm font-semibold text-[#1E1B4B]">
              {separate
                ? "Only people who changed this stage"
                : "Shared stage — who last updated it"}
            </p>
            {!separate && leadStage ? (
              <span className="mt-2 inline-flex rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold capitalize text-orange-600">
                {formatStage(leadStage)}
              </span>
            ) : null}
          </div>
          <div>
            <p className="text-xs font-medium text-[#6B7280]">Updated by</p>
            <p className="mt-1.5 text-sm font-bold text-[#1E1B4B]">
              {rows.length}
            </p>
          </div>
          {lastTouched ? (
            <div>
              <p className="text-xs font-medium text-[#6B7280]">
                Latest activity
              </p>
              <p className="mt-1.5 text-sm font-bold capitalize text-[#1E1B4B]">
                {lastTouched.user_name || "—"} ·{" "}
                {formatStage(lastTouched.stage)}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <div className="max-h-[360px] overflow-auto rounded-xl border border-[#EEF0F5]">
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 z-[1] bg-[#F8FAFC]">
            <tr>
              <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
                Assignee
              </th>
              <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
                Designation
              </th>
              <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
                Their stage
              </th>
              <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
                Updated by
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-sm text-[#9CA3AF]"
                >
                  No one has updated this lead&apos;s stage yet
                </td>
              </tr>
            ) : (
              rows.map((item, index) => {
                const name = item.user_name || "Unknown user";
                const designation = item.designation || "—";
                const shortRole =
                  designation !== "—"
                    ? designation
                        .split(" ")
                        .map((w: string) => w[0])
                        .join("")
                        .slice(0, 10)
                    : "";
                const initials =
                  String(name)
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((p: string) => p[0]?.toUpperCase())
                    .join("") || "?";
                const avatar = avatarPalette[index % avatarPalette.length];
                const stageLabel = formatStage(item.stage);
                const updaterId = normId(item.updatedBy);
                const selfUpdate =
                  updaterId && updaterId === normId(item.user_id);
                const updatedByLabel = item.updatedByName
                  ? selfUpdate
                    ? `${item.updatedByName} (self)`
                    : item.updatedByName
                  : updaterId
                    ? memberList.find(
                        (e) => normId(e._id || e.id) === updaterId
                      )?.name || "—"
                    : "Not updated yet";
                const updatedAtLabel = formatWhen(item.updatedAt);

                return (
                  <tr
                    key={`${item.user_id || "row"}-${index}`}
                    className="border-t border-[#EEF0F5]"
                  >
                    <td className="px-4 py-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                          style={{
                            backgroundColor: avatar.bg,
                            color: avatar.color,
                          }}
                        >
                          {initials}
                        </span>
                        <span className="min-w-0 truncate text-sm font-semibold text-[#111827]">
                          {name}
                          {shortRole ? (
                            <span className="font-normal text-[#9CA3AF]">
                              {" "}
                              ({shortRole})
                            </span>
                          ) : null}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#374151]">
                      {designation}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-[#F9F0F4] px-2.5 py-1 text-xs font-semibold capitalize text-[#A02448]">
                        {stageLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold capitalize text-[#111827]">
                          {updatedByLabel}
                        </p>
                        {updatedAtLabel ? (
                          <p className="mt-0.5 text-[11px] text-[#9CA3AF]">
                            {updatedAtLabel}
                          </p>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StageDetailsPanel;
